import { AnyAction } from 'redux';
import { FormikErrors } from 'formik';
import { HandledError } from '@synergy/api';
import { UserRole } from '@synergy/models';
import lodashSet from 'lodash/set';

import {
    CONTACT_DETAIL_PERMISSIONS_DISABLE_LOGIN,
    CONTACT_DETAIL_PERMISSIONS_ERROR_DATA,
    CONTACT_DETAIL_PERMISSIONS_RECEIVED,
    CONTACT_DETAIL_PERMISSIONS_UPDATE,
} from './contact-detail-permissions-actions';
import { ContactPermissions } from '../models/contact-permissions';
import { ContactPermissionsFormValues } from '../models/contact-permissions-form-values';
import { EmailErrorCode, EmailErrorCodeMessageId } from '../../../../models/email-error-code';

export interface ContactDetailPermissionsReducerState {
    permissions: ContactPermissions | null;
    /**
     * The permissions as the server last handed them over, with the flag that
     * goes with them (#2159). The copy above cannot serve: leaving the tab
     * parks its input right there.
     */
    baseline?: { permissions: ContactPermissions | null; hasUser: boolean };
    hasUser: boolean;
    password?: string;
    isValid: boolean;
    isDirty: boolean;
    errors: FormikErrors<ContactPermissionsFormValues>;
}

const initialContactPermissionsReducerState: ContactDetailPermissionsReducerState = {
    permissions: null,
    hasUser: false,
    isValid: true,
    isDirty: false,
    errors: {},
};

export const contactDetailPermissionsReducer = (
    state: ContactDetailPermissionsReducerState = initialContactPermissionsReducerState,
    action: AnyAction
): ContactDetailPermissionsReducerState => {
    switch (action.type) {
        case CONTACT_DETAIL_PERMISSIONS_RECEIVED: {
            // A permission record without an e-mail is a contact without a
            // login. The baseline said otherwise, so opening the tab was
            // already a difference and the mask asked about input nobody had
            // given (#2212, R-16).
            const hasUser = !!action['permissions'].email;

            return {
                ...state,
                permissions: action['permissions'],
                // The same values, kept apart from the ones the tab edits.
                baseline: { permissions: action['permissions'], hasUser },
                hasUser,
            };
        }

        case CONTACT_DETAIL_PERMISSIONS_UPDATE: {
            const { formData, isDirty, isValid, errors } = action;

            const formRoles: Record<UserRole, boolean> = formData.roles;
            const nextRoles = Object.entries(formRoles)
                .filter(entry => entry[1]) // IsActive
                .map(entry => entry[0] as UserRole); // Role

            return {
                ...state,
                permissions: {
                    ...state.permissions,
                    locale: formData.locale,
                    mainEmail: formData.email,
                    roles: nextRoles,
                    shopAdminOfMembers: state.permissions?.shopAdminOfMembers?.map(member => ({
                        ...member,
                        isShopAdmin: (formData.shopAdminAccountIds ?? []).includes(member.id),
                    })),
                },
                password: formData.password,
                isDirty: state.isDirty || isDirty,
                isValid,
                errors,
            };
        }

        case CONTACT_DETAIL_PERMISSIONS_ERROR_DATA: {
            const formErrors: FormikErrors<ContactPermissionsFormValues> = { ...state.errors };
            const errorData = action['errorData'] as HandledError<EmailErrorCode>[];
            errorData.forEach(error => {
                if (error.context) {
                    lodashSet(formErrors, error.property, error.context);
                } else {
                    lodashSet(formErrors, error.property, EmailErrorCodeMessageId[error.error]);
                }
            });

            return {
                ...state,
                isValid: false,
                errors: formErrors,
            };
        }

        case CONTACT_DETAIL_PERMISSIONS_DISABLE_LOGIN: {
            return {
                ...state,
                permissions: null,
                hasUser: false,
                password: undefined,
                // The login is already gone on the server.
                baseline: { permissions: null, hasUser: false },
            };
        }

        default: {
            return state;
        }
    }
};
